import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { PersonaSize } from "office-ui-fabric-react";
import { ApplicationState } from "../../../../store";
import { ApprovalRequest } from "../../../../store/ApprovalCenterStore";
import * as StageTransitionApprovalStore from "../../../../store/StageTransitionApprovalStore";
import { StageTransitionApprovalDetails } from "../../../../store/StageTransitionApprovalStore";
import { FilterKeys } from "../../../../store/filters";
import { IUserInfo, mapServerEntityType } from "../../../../entities/common";
import * as Metadata from "../../../../entities/Metadata";
import { nameof } from "../../../../store/services/metadataService";
import { ApprovalRequestAttrs } from "../../../../store/ApprovalCenterStore";
import ApprovalTabContent from "../../ApprovalTabContent";
import {
    ApprovalTabActions,
    ApprovalTabSpec,
    ApprovalTabState,
    buildApprovalTabActions,
    buildSaveApprovalTabSetting,
    selectApprovalTabState
} from "../../approvalTabBase";
import { ControlSettings, Views } from "../../../../store/services/viewSaver";
import {
    stageTransitionFakeFields,
    stageTransitionFilterableFakeFields,
    Title,
    ProjectManagerField,
    TransitionFromStageField,
    TransitionToStageField,
    DecisionDateField,
    ApproverField,
    DecisionMakersField,
    ApproverCommentField
} from "./stageTransitionApprovalFields";
import { commonFakeFieldValueGetters, commonFakeFieldRenderers } from "../../commonFakeFields";
import EntityName from "../../../views/list/columns/EntityName";
import StageTitle from "../../../views/list/columns/subentity/processStage/StageTitle";
import ImagePersona from "../../../common/ImagePersona";
import ResourceFormatter from "../../../common/formatters/ResourceFormatter";

type StageTransitionApprovalRequest = ApprovalRequest<StageTransitionApprovalDetails>;

const defaultDisplayFieldNames = [
    Title.name,
    nameof<ApprovalRequestAttrs>("Status"),
    ProjectManagerField.name,
    TransitionFromStageField.name,
    TransitionToStageField.name,
    nameof<ApprovalRequestAttrs>("Submitter"),
    nameof<ApprovalRequestAttrs>("SubmittedDate"),
    DecisionDateField.name,
    ApproverField.name,
    DecisionMakersField.name,
    ApproverCommentField.name
];

const mandatoryFieldNames = [Title.name, nameof<ApprovalRequestAttrs>("Status")];

type StateProps = ApprovalTabState<StageTransitionApprovalDetails>;

type ActionProps = ApprovalTabActions<typeof StageTransitionApprovalStore.actionCreators>;

type Props = StateProps & ActionProps;

const approveDialogContent = {
    title: "Approve Stage Transition",
    subText: "Are you sure you would like to approve the transition to the next stage?"
};

const rejectDialogContent = {
    title: "Reject Stage Transition",
    subText: "Are you sure you would like to reject the transition to the next stage?"
};

const StageTransitionApprovalTab = (props: Props) => {
    const {
        requests,
        isLoading,
        isReadonlyMode,
        isSettingsLoading,
        settings,
        actions,
        serverFields,
        filters,
        activeFilter,
        canManageConfiguration,
        filtersActions,
        preFilterId,
        currentUserId
    } = props;

    const controlSettings: ControlSettings = useMemo(
        () => ({
            ...settings,
            viewType: Views.List
        }),
        [settings]
    );

    const displayFieldNames = useMemo(() => {
        const saved: string[] | undefined = settings?.displayFields;
        return saved?.length ? saved : defaultDisplayFieldNames;
    }, [settings?.displayFields]);

    const configurableFields = useMemo(
        () => [...serverFields, ...stageTransitionFakeFields].filter(f => defaultDisplayFieldNames.includes(f.name)),
        [serverFields]
    );

    const saveSetting = useMemo(() => buildSaveApprovalTabSetting(actions.saveSettings), [actions]);

    const handleDisplayFieldsChange = useCallback((fields: string[]) => saveSetting.displayFields(fields), [saveSetting]);

    const fieldValueExtractor = useCallback((item: StageTransitionApprovalRequest, field: Metadata.Field) => {
        if (!field.isFake) {
            return item.attributes[field.name];
        }

        const details = item.details;
        switch (field.name) {
            case Title.name:
                return details?.project?.name ?? "";
            case ProjectManagerField.name:
                return details?.projectManager ?? [];
            case TransitionFromStageField.name:
                return details?.stage?.name ?? "";
            case TransitionToStageField.name:
                return details?.nextStage?.name ?? "";
            case DecisionDateField.name:
                return commonFakeFieldValueGetters[DecisionDateField.name]!(item, currentUserId);
            case ApproverField.name:
                return item.attributes.Approvers ?? [];
            case DecisionMakersField.name:
                return item.decisions.map(d => d.approver);
            case ApproverCommentField.name:
                return commonFakeFieldValueGetters[ApproverCommentField.name]!(item, currentUserId);
            default:
                return undefined;
        }
    }, []);

    const onItemRender = useCallback(
        (entity: StageTransitionApprovalRequest, index: number, field: Metadata.Field, defaultRender: () => JSX.Element) => {
            const details = entity.details;

            if (field.name === Title.name) {
                return (
                    <EntityName
                        name={details?.project?.name ?? ""}
                        url={_buildProjectUrl(details)}
                        imageClassName="proj-logo"
                    />
                );
            }

            if (field.name === ProjectManagerField.name) {
                const managers: IUserInfo[] = details?.projectManager ?? [];
                if (managers.length === 0) {
                    return <span></span>;
                }
                return (
                    <div className="align-center" style={{ gap: 4 }}>
                        {managers.map(m => (
                            <ImagePersona
                                key={m.id}
                                size={PersonaSize.size24}
                                info={m}
                                text={m.fullName ?? ""}
                                hidePersonaDetails={managers.length > 1}
                            />
                        ))}
                    </div>
                );
            }

            if (field.name === TransitionFromStageField.name) {
                return <StageTitle name={details?.stage?.name ?? ""} color={details?.stage?.color ?? ""} />;
            }

            if (field.name === TransitionToStageField.name) {
                return <StageTitle name={details?.nextStage?.name ?? ""} color={details?.nextStage?.color ?? ""} />;
            }

            if (field.name === ApproverField.name) {
                const approvers: IUserInfo[] = entity.attributes.Approvers ?? [];
                if (approvers.length === 0) {
                    return <span></span>;
                }
                return (
                    <div className="align-center" style={{ gap: 4 }}>
                        {approvers.map(a => (
                            <ImagePersona
                                key={a.id}
                                size={PersonaSize.size24}
                                info={a}
                                text={a.fullName ?? ""}
                                hidePersonaDetails={approvers.length > 1}
                            />
                        ))}
                    </div>
                );
            }

            if (field.name === DecisionMakersField.name) {
                const decisionMakers = entity.decisions.map(d => d.approver);
                if (!decisionMakers.length) {
                    return <span />;
                }
                return <ResourceFormatter resource={decisionMakers} withNavigation={false} coinSize={PersonaSize.size24} />;
            }

            const commonRenderer = commonFakeFieldRenderers[field.name];
            if (commonRenderer) {
                return commonRenderer(entity, currentUserId);
            }

            return defaultRender();
        },
        []
    );

    return (
        <ApprovalTabContent
            requests={requests}
            isLoading={isLoading}
            isReadonlyMode={isReadonlyMode}
            actions={actions}
            serverFields={serverFields}
            fakeFields={stageTransitionFakeFields}
            filterableFakeFields={stageTransitionFilterableFakeFields}
            displayFields={displayFieldNames}
            configurableFields={configurableFields}
            mandatoryFields={mandatoryFieldNames}
            onDisplayFieldsChange={handleDisplayFieldsChange}
            filters={filters}
            activeFilter={activeFilter}
            saveSetting={saveSetting}
            canManageConfiguration={canManageConfiguration}
            filtersActions={filtersActions}
            fieldValueExtractor={fieldValueExtractor}
            onItemRender={onItemRender}
            controlSettings={controlSettings}
            onSaveSettings={isSettingsLoading ? undefined : actions.saveSettings}
            preFilterId={preFilterId}
            approveDialogContent={approveDialogContent}
            rejectDialogContent={rejectDialogContent}
        />
    );
};


const _buildProjectUrl = (details: StageTransitionApprovalDetails | null) => {
    const entityType = details && mapServerEntityType[details.entityType];
    return entityType ? `/${entityType}/${details!.entityId}` : undefined;
};

const mapStateToProps = (state: ApplicationState): StateProps =>
    selectApprovalTabState(state, state.stageTransitionApprovals, FilterKeys.ProjectStageApproval);

const mapDispatchToProps = buildApprovalTabActions(
    StageTransitionApprovalStore.actionCreators,
    FilterKeys.ProjectStageApproval
);

const ConnectedStageTransitionApprovalTab = connect(mapStateToProps, mapDispatchToProps)(StageTransitionApprovalTab);

export default ConnectedStageTransitionApprovalTab;

export const stageTransitionTabSpec: ApprovalTabSpec = {
    key: "projectStageTransition",
    label: "Project Stage",
    isVisible: state => !!state.user.canApprove?.stageTransition,
    selectPendingCount: state => state.stageTransitionApprovals.pendingCount,
    settings: {
        markLoading: StageTransitionApprovalStore.actionCreators.markSettingsLoading,
        applyLoaded: StageTransitionApprovalStore.actionCreators.applyLoadedSettings
    },
    loadPendingCount: StageTransitionApprovalStore.actionCreators.loadPendingCount,
    render: () => <ConnectedStageTransitionApprovalTab />
};
